/**
 * Canadian Payroll Calculator API Service
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

export interface PreTaxDeductions {
  rrsp?: number;
  pension?: number;
  union_dues?: number;
}

export interface PayrollRequest {
  gross_salary: number;
  province: string;
  pay_frequency: string;
  year: number;
  num_employees: number;
  pre_tax_deductions?: PreTaxDeductions;
}

export interface HourlyPayrollRequest {
  hourly_rate: number;
  hours_per_week: number;
  overtime_hours: number;
  overtime_rate: number;
  province: string;
  pay_frequency: string;
  year: number;
  num_employees: number;
  pre_tax_deductions?: PreTaxDeductions;
}

export interface JournalEntryRequest {
  payroll_result: PayrollResponse;
  accounts: Record<string, number | null>;
}

export interface PayrollPerPeriod {
  gross_pay: number;
  federal_tax: number;
  provincial_tax: number;
  pension_employee: number;
  pension2_employee: number;
  ei_employee: number;
  qpip_employee: number;
  total_deductions: number;
  net_pay: number;
  pension_employer: number;
  pension2_employer: number;
  ei_employer: number;
  qpip_employer: number;
  total_employer_contributions: number;
  total_cost_to_employer: number;
  rrsp_deduction?: number;
  pension_plan_deduction?: number;
  union_dues_deduction?: number;
  total_pre_tax_deductions?: number;
}

export interface PayrollAnnual {
  gross_salary: number;
  federal_tax: number;
  provincial_tax: number;
  pension_base_employee: number;
  pension2_employee: number;
  total_pension_employee: number;
  ei_employee: number;
  qpip_employee: number;
  total_employee_deductions: number;
  net_pay: number;
  pension_base_employer: number;
  pension2_employer: number;
  total_pension_employer: number;
  ei_employer: number;
  qpip_employer: number;
  total_employer_contributions: number;
  total_cost_to_employer: number;
  rrsp_deduction?: number;
  pension_plan_deduction?: number;
  union_dues_deduction?: number;
  total_pre_tax_deductions?: number;
  taxable_income?: number;
}

export interface PayrollRatesInfo {
  pension_label: string;
  pension_rate: number;
  pension2_rate: number;
  pension_ympe: number;
  pension_yampe: number;
  pension_exemption: number;
  ei_employee_rate: number;
  ei_employer_rate: number;
  ei_max_insurable: number;
  is_quebec: boolean;
  qpip_employee_rate?: number;
  qpip_employer_rate?: number;
  qpip_max_insurable?: number;
}

export interface PayrollEffectiveRates {
  total_tax_rate: number;
  federal_rate: number;
  provincial_rate: number;
  pension_rate: number;
  ei_rate: number;
  employer_burden_rate: number;
}

export interface PayrollMultiEmployee {
  num_employees: number;
  total_salaries: number;
  total_employer_pension: number;
  total_employer_ei: number;
  total_employer_qpip: number;
  total_employer_contributions: number;
  total_payroll_cost: number;
}

export interface HourlyDetails {
  hourly_rate: number;
  hours_per_week: number;
  regular_hours: number;
  overtime_hours_per_week: number;
  overtime_rate_multiplier: number;
  overtime_threshold: number;
  regular_weekly_pay: number;
  overtime_weekly_pay: number;
  total_weekly_pay: number;
  regular_pay_per_period: number;
  overtime_pay_per_period: number;
  annual_regular_pay: number;
  annual_overtime_pay: number;
  annual_gross_pay: number;
}

export interface JournalEntryLine {
  line_number: number;
  account_id: number | null;
  account_label: string;
  description: string;
  debit_amount: number;
  credit_amount: number;
}

export interface JournalEntryPreview {
  description: string;
  entry_type: string;
  lines: JournalEntryLine[];
  total_debits: number;
  total_credits: number;
  is_balanced: boolean;
}

export interface PayrollResponse {
  per_period: PayrollPerPeriod;
  annual: PayrollAnnual;
  rates_info: PayrollRatesInfo;
  effective_rates: PayrollEffectiveRates;
  multi_employee: PayrollMultiEmployee | null;
  province: string;
  pay_frequency: string;
  periods_per_year: number;
  year: number;
  pension_label: string;
  hourly_details?: HourlyDetails;
  pay_type?: 'hourly' | 'salaried';
}

// ============ Employee Types ============

export interface PostTaxDeductions {
  life_insurance?: number;
  health_dental?: number;
  other?: number;
  other_label?: string;
}

export interface PayrollEmployee {
  id: number;
  user_id: number;
  name: string;
  pay_type: 'salary' | 'hourly';
  gross_salary: number | null;
  hourly_rate: number | null;
  hours_per_period: number | null;
  province: string;
  pay_frequency: string;
  pre_tax_deductions: PreTaxDeductions;
  post_tax_deductions: PostTaxDeductions;
  created_at: string | null;
  updated_at: string | null;
}

export interface SaveEmployeeRequest {
  name: string;
  pay_type: 'salary' | 'hourly';
  gross_salary?: number | null;
  hourly_rate?: number | null;
  hours_per_period?: number | null;
  province: string;
  pay_frequency: string;
  pre_tax_deductions?: PreTaxDeductions;
  post_tax_deductions?: PostTaxDeductions;
}

class PayrollAPI {
  private async fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE}/backend/payroll${endpoint}`, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'API request failed');
    }

    return data.data;
  }

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = 'Bearer ' + token;
    }
    return headers;
  }

  async calculate(request: PayrollRequest): Promise<PayrollResponse> {
    return this.fetchAPI<PayrollResponse>('/calculate', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async calculateHourly(request: HourlyPayrollRequest): Promise<PayrollResponse> {
    return this.fetchAPI<PayrollResponse>('/calculate-hourly', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async generateJournalEntry(
    payrollResult: PayrollResponse,
    accounts: Record<string, number | null>,
  ): Promise<JournalEntryPreview> {
    return this.fetchAPI<JournalEntryPreview>('/generate-journal-entry', {
      method: 'POST',
      body: JSON.stringify({
        payroll_result: payrollResult,
        accounts,
      }),
    });
  }

  async getProvinces(): Promise<string[]> {
    return this.fetchAPI<string[]>('/provinces');
  }

  async getOvertimeThresholds(): Promise<Record<string, number>> {
    return this.fetchAPI<Record<string, number>>('/overtime-thresholds');
  }

  // ============ Employee CRUD ============

  async listEmployees(): Promise<PayrollEmployee[]> {
    const response = await fetch(`${API_BASE}/backend/payroll/employees`, {
      headers: this.getAuthHeaders(),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to list employees');
    }
    return data.data;
  }

  async getEmployee(id: number): Promise<PayrollEmployee> {
    const response = await fetch(`${API_BASE}/backend/payroll/employees/${id}`, {
      headers: this.getAuthHeaders(),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to get employee');
    }
    return data.data;
  }

  async saveEmployee(employee: SaveEmployeeRequest): Promise<PayrollEmployee> {
    const response = await fetch(`${API_BASE}/backend/payroll/employees`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(employee),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to save employee');
    }
    return data.data;
  }

  async updateEmployee(id: number, employee: Partial<SaveEmployeeRequest>): Promise<PayrollEmployee> {
    const response = await fetch(`${API_BASE}/backend/payroll/employees/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(employee),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to update employee');
    }
    return data.data;
  }

  async deleteEmployee(id: number): Promise<void> {
    const response = await fetch(`${API_BASE}/backend/payroll/employees/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to delete employee');
    }
  }
}

export const payrollAPI = new PayrollAPI();
export default payrollAPI;
